const HISTORY_CACHE_BROADCAST_CHANNEL = 'issen-history-cache';

type HistoryCacheClearBroadcastMessage = {
  type: 'clear';
  sourceId: string;
};

type HistoryCacheTitleBroadcastMessage = {
  type: 'titleUpdated';
  sourceId: string;
  userId: string;
  agentId: string;
  sessionId: string;
  title: string;
};

export type HistoryCacheBroadcastMessage =
  | HistoryCacheClearBroadcastMessage
  | HistoryCacheTitleBroadcastMessage;

export interface HistoryCacheBroadcastAdapter {
  publish(message: HistoryCacheBroadcastMessage): void;
  subscribe(listener: (message: HistoryCacheBroadcastMessage) => void): () => void;
  close(): void;
}

class NativeHistoryCacheBroadcast implements HistoryCacheBroadcastAdapter {
  private readonly listeners = new Set<(message: HistoryCacheBroadcastMessage) => void>();

  constructor(private readonly channel: BroadcastChannel) {
    this.channel.onmessage = (event: MessageEvent<unknown>) => {
      const message = event.data;
      if (
        message === null
        || typeof message !== 'object'
        || !('type' in message)
        || !('sourceId' in message)
        || typeof message.sourceId !== 'string'
      ) {
        return;
      }
      if (message.type === 'clear') {
        for (const listener of this.listeners) {
          listener(message as HistoryCacheClearBroadcastMessage);
        }
        return;
      }
      if (
        message.type !== 'titleUpdated'
        || !('userId' in message)
        || typeof message.userId !== 'string'
        || !('agentId' in message)
        || typeof message.agentId !== 'string'
        || !('sessionId' in message)
        || typeof message.sessionId !== 'string'
        || !('title' in message)
        || typeof message.title !== 'string'
      ) {
        return;
      }
      for (const listener of this.listeners) {
        listener(message as HistoryCacheTitleBroadcastMessage);
      }
    };
  }

  publish(message: HistoryCacheBroadcastMessage): void {
    this.channel.postMessage(message);
  }

  subscribe(listener: (message: HistoryCacheBroadcastMessage) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  close(): void {
    this.listeners.clear();
    this.channel.close();
  }
}

export const createBrowserHistoryCacheBroadcast = (): HistoryCacheBroadcastAdapter | null => {
  if (typeof globalThis.BroadcastChannel !== 'function') {
    return null;
  }
  return new NativeHistoryCacheBroadcast(
    new globalThis.BroadcastChannel(HISTORY_CACHE_BROADCAST_CHANNEL),
  );
};
